import type { Project, CreateProjectRequest, UpdateProjectRequest, SpecSummary, SpecDetail, StorySummary, BacklogItem, DocEntry, PathValidationResult, GitStatus, GitActionResult, GitCommitRequest } from '../types/project.js';

const BASE_URL = '/api/projects';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(body.error?.message ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getProjects(status?: string): Promise<Project[]> {
  const params = status ? `?status=${encodeURIComponent(status)}` : '';
  return fetchJson<Project[]>(`${BASE_URL}${params}`);
}

export async function getRecentProjects(): Promise<Project[]> {
  return fetchJson<Project[]>(`${BASE_URL}/recent`);
}

export async function getProject(id: number): Promise<Project> {
  return fetchJson<Project>(`${BASE_URL}/${id}`);
}

export async function createProject(data: CreateProjectRequest): Promise<Project> {
  return fetchJson<Project>(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateProject(id: number, data: UpdateProjectRequest): Promise<Project> {
  return fetchJson<Project>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteProject(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(body.error?.message ?? `Request failed: ${response.status}`);
  }
}

export async function markProjectOpened(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}/open`, { method: 'POST' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(body.error?.message ?? `Request failed: ${response.status}`);
  }
}

export async function getSpecs(projectId: number): Promise<SpecSummary[]> {
  return fetchJson<SpecSummary[]>(`${BASE_URL}/${projectId}/specs`);
}

export async function getSpecDetail(projectId: number, specId: string): Promise<SpecDetail> {
  return fetchJson<SpecDetail>(`${BASE_URL}/${projectId}/specs/${encodeURIComponent(specId)}`);
}

export async function getSpecStories(projectId: number, specId: string): Promise<StorySummary[]> {
  return fetchJson<StorySummary[]>(`${BASE_URL}/${projectId}/specs/${encodeURIComponent(specId)}/stories`);
}

export async function getBacklog(projectId: number): Promise<BacklogItem[]> {
  return fetchJson<BacklogItem[]>(`${BASE_URL}/${projectId}/backlog`);
}

export async function getDocs(projectId: number): Promise<DocEntry[]> {
  return fetchJson<DocEntry[]>(`${BASE_URL}/${projectId}/docs`);
}

export async function getProjectFiles(projectId: number, query?: string): Promise<string[]> {
  const params = query ? `?q=${encodeURIComponent(query)}` : '';
  return fetchJson<string[]>(`${BASE_URL}/${projectId}/files${params}`);
}

export async function validatePath(projectPath: string): Promise<PathValidationResult> {
  return fetchJson<PathValidationResult>(`${BASE_URL}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: projectPath }),
  });
}

// Git operations

export async function getGitStatus(projectId: number): Promise<GitStatus> {
  return fetchJson<GitStatus>(`${BASE_URL}/${projectId}/git/status`);
}

export async function gitPull(projectId: number): Promise<GitActionResult> {
  return fetchJson<GitActionResult>(`${BASE_URL}/${projectId}/git/pull`, { method: 'POST' });
}

export async function gitPush(projectId: number): Promise<GitActionResult> {
  return fetchJson<GitActionResult>(`${BASE_URL}/${projectId}/git/push`, { method: 'POST' });
}

export async function gitCommit(projectId: number, data: GitCommitRequest): Promise<GitActionResult> {
  return fetchJson<GitActionResult>(`${BASE_URL}/${projectId}/git/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}
